import { useCallback, useEffect, useRef } from 'react';
import { type SubscriptionTerm } from '@cx-sdk/subs';
import { SUBSCRIPTION_TERM } from '@cx-sdk/subs/schema';
import { type SubsProduct } from '@cx-web/domain-types';
import { REPUB_CONFIG, type SubscriptionProductConfig, type SupportedRepubName } from '@cx-web/repub-config';
import { useSearchParams } from '@remix-run/react';

import { type Repub } from '@cx-modules/constants';

import {
  trackLandingSubscriptionClick,
  trackSubscribeConfirmed,
} from '~/apps/product-plus/components/BettingInsights/tracking.ts';
import { type Selection, useSubscribe } from '~/apps/product-plus/components/Subscribe/hooks/use-subscribe.ts';
import { URL_PARAMS } from '~/apps/product-plus/constants/url-params.ts';
import { CX_LOGIN_SUCCESS } from '~/apps/product-plus/events/CXLoginSuccessEvent';
import { useAuthModal, VIEW_SIGNUP, VIEW_VERIFY_PAYMENT } from '~/apps/product-plus/providers/auth-modal-provider';

/**
 * Configuration for subscription flow hook
 * Following Dependency Inversion Principle
 */
export interface SubscriptionFlowConfig {
  canUpgrade?: boolean;
  cancelPayment: string | null;
  isLoggedIn: boolean;
  isSubscribed: boolean;
  productPrice?: string | null;
  redirectPath?: string;
  repub: Repub;
  repubPath: string;
  requestContext?: {
    user: {
      subscriptions: Array<{ status: string }>;
    };
  };
  subscriptionTerm?: SubscriptionTerm;
  userHasCancelledSubscription?: boolean;
  userId?: string;
  verifyPayment: string | null;
}

/**
 * Product selection handler configuration
 */
export interface ProductSelectionConfig {
  availableProducts?: SubsProduct[];
  getSelectionLabel?: (product: SubsProduct) => 'Essential' | 'Premium';
  onBeforeSubscribe?: (product: SubsProduct, selection: Selection) => void | boolean;
  onPostLoginSubscribe?: () => SubsProduct | undefined;
  onTrackSubscriptionClick?: (product: SubsProduct, metadata?: any) => void;
}

/**
 * Custom hook to manage subscription flow logic
 * Implements Single Responsibility Principle: Only handles subscription orchestration
 *
 * @example
 * ```tsx
 * const { handleProductClick } = useSubscriptionFlow(
 *   config,
 *   { availableProducts, onPostLoginSubscribe }
 * );
 * ```
 */
export function useSubscriptionFlow(config: SubscriptionFlowConfig, productConfig: ProductSelectionConfig) {
  const { close, openAtView } = useAuthModal();
  const selectionRef = useRef<SubsProduct | undefined>();
  const [searchParams] = useSearchParams();
  const isAttemptSubscribeOnPageLoad = searchParams.get(URL_PARAMS.IS_ATTEMPT_SUBSCRIBE);

  const userHasCancelledSubscription =
    config.userHasCancelledSubscription ??
    config.requestContext?.user.subscriptions.some(({ status }) => status === 'CANCELLED') ??
    false;

  // Handle successful payment confirmation
  const onConfirmed = useCallback(() => {
    if (config.userId) {
      trackSubscribeConfirmed({
        price: config.productPrice ?? '',
        term: config.subscriptionTerm,
        userId: config.userId,
      });
    }

    close();
    window.location.href =
      config.redirectPath ??
      REPUB_CONFIG[config.repub as SupportedRepubName].plus!.landingPage.defaultPageForSubscribedUser(config.repubPath);
  }, [config, close]);

  // Handle payment verification start
  const onVerifying = () => openAtView(VIEW_VERIFY_PAYMENT);

  // Initialize subscription hook
  const { subscribe } = useSubscribe({
    canUpgrade: config.canUpgrade,
    cancelPayment: config.cancelPayment,
    onConfirmed,
    onVerifying,
    redirectPath: config.redirectPath,
    repub: config.repub,
    shouldLogin: !config.isLoggedIn,
    verifyPayment: config.verifyPayment,
    userHasCancelledSubscription,
  });

  // Handle product selection and initiate subscription flow
  const handleProductClick = useCallback(
    (selection: Selection, trackingMetadata?: { isUpgrade?: boolean }) => {
      const product = productConfig.availableProducts?.find(p => p.productId === selection.productId) as
        | SubsProduct
        | undefined;

      if (!product) {
        throw new Error('Product not found');
      }

      selectionRef.current = product;

      // Allow customization before subscribing
      if (productConfig.onBeforeSubscribe) {
        const shouldContinue = productConfig.onBeforeSubscribe(product, selection);
        if (shouldContinue === false) {
          return;
        }
      }

      const subscriptionConfig = REPUB_CONFIG[config.repub as SupportedRepubName]?.plus?.subscription;
      const products = subscriptionConfig?.products as Record<string, SubscriptionProductConfig> | undefined;
      const productConfigData = products?.[product.productId];
      const hasExternalUrl = Boolean(productConfigData?.externalUrl);

      // Track subscription click (custom or default)
      if (productConfig.onTrackSubscriptionClick) {
        productConfig.onTrackSubscriptionClick(product, trackingMetadata);
      } else {
        const selectionLabel = productConfig.getSelectionLabel
          ? productConfig.getSelectionLabel(product)
          : product.term === SUBSCRIPTION_TERM.MONTHLY
            ? 'Essential'
            : 'Premium';

        trackLandingSubscriptionClick({
          price: product.priceFormatted,
          repub: config.repub,
          selection: selectionLabel,
          term: product.term,
          ...trackingMetadata,
        });
      }

      // Skip modal flow for external URLs (tracking already happened above)
      if (hasExternalUrl) {
        return;
      }

      // Execute subscription flow (only for internal products)
      if (config.isLoggedIn) {
        if (config.isSubscribed) {
          return;
        } else {
          subscribe(product);
        }
      } else {
        openAtView(VIEW_SIGNUP);
      }
    },
    [productConfig, config.repub, config.isLoggedIn, config.isSubscribed, subscribe, openAtView],
  );

  // Handle login success event - standardized behavior
  const onLoginSuccess = useCallback((_e: CustomEvent) => {
    // Standard behavior: redirect with isAttemptSubscribe parameter if product selected
    if (selectionRef.current) {
      const url = new URL(window.location.href, window.location.origin);
      url.searchParams.set(URL_PARAMS.IS_ATTEMPT_SUBSCRIBE, 'true');
      // Store selected product ID to restore after redirect
      url.searchParams.set(URL_PARAMS.PRODUCT_ID, selectionRef.current.productId);
      window.location.href = url.toString();
    } else {
      window.location.reload();
    }
  }, []);

  // Setup event listener for login success
  useEffect(() => {
    window.addEventListener(CX_LOGIN_SUCCESS, onLoginSuccess);
    return () => {
      window.removeEventListener(CX_LOGIN_SUCCESS, onLoginSuccess);
    };
  }, [onLoginSuccess]);

  // Watch for URL parameter and trigger subscription after login redirect
  useEffect(() => {
    if (isAttemptSubscribeOnPageLoad === 'true' && productConfig.onPostLoginSubscribe) {
      const productToSubscribe = productConfig.onPostLoginSubscribe();
      if (productToSubscribe) {
        subscribe(productToSubscribe);
      }
    }
  }, [isAttemptSubscribeOnPageLoad, productConfig, subscribe]);

  return {
    handleProductClick,
    subscribe,
    userHasCancelledSubscription,
  };
}
